import { clsx } from 'clsx';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { colorClasses } from '../../lib/colors';
import { type DismissReason, useDismiss, usePopoverPosition } from './overlay';

export type MultiSelectOption = {
  value: string;
  label: string;
  /** Color token → leading dot (stages, tiers, tags). */
  color?: string;
  /** Muted text on the right (e.g. a count). */
  hint?: ReactNode;
};

export type MultiSelectProps = {
  /** Filter name shown on the trigger, e.g. "Tier". */
  label: string;
  options: readonly MultiSelectOption[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  /** Show the search box. Defaults to true when there are more than 7 options. */
  searchable?: boolean;
  size?: 'sm' | 'md';
  align?: 'start' | 'end';
  disabled?: boolean;
  className?: string;
};

/**
 * Checkbox dropdown for list filters. Trigger reads "Tier" / "Tier: Hot" / "Tier: 3 selected".
 * ↑/↓ move, Space/Enter toggle, Esc closes; typing filters when searchable.
 */
export function MultiSelect({
  label,
  options,
  value,
  onChange,
  searchable,
  size = 'sm',
  align = 'start',
  disabled,
  className,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();
  const placement = usePopoverPosition(triggerRef, popoverRef, open, align);
  const showSearch = searchable ?? options.length > 7;
  const selected = useMemo(() => new Set(value), [value]);

  const close = useCallback((reason: DismissReason) => {
    setOpen(false);
    if (reason === 'escape') triggerRef.current?.focus();
  }, []);
  useDismiss([triggerRef, popoverRef], open, close);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    return q ? options.filter((o) => o.label.toLocaleLowerCase().includes(q)) : options;
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    const frame = requestAnimationFrame(() => {
      popoverRef.current?.querySelector<HTMLElement>(showSearch ? 'input' : '[role="listbox"]')?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, showSearch]);

  const toggle = (optionValue: string) => {
    onChange(selected.has(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue]);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (filtered.length === 0) return;
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const next = (active + delta + filtered.length) % filtered.length;
      setActive(next);
      listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' || (event.key === ' ' && !showSearch)) {
      event.preventDefault();
      const option = filtered[active];
      if (option) toggle(option.value);
    }
  };

  const selectedLabels = options.filter((o) => selected.has(o.value)).map((o) => o.label);
  const summary =
    selectedLabels.length === 0 ? null : selectedLabels.length === 1 ? selectedLabels[0] : `${selectedLabels.length} selected`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          'inline-flex max-w-64 items-center gap-1 rounded-md border px-2.5 font-medium whitespace-nowrap shadow-xs transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500 disabled:opacity-50',
          size === 'sm' ? 'h-7 text-xs' : 'h-8 text-[13px]',
          summary
            ? 'border-ember-300 bg-ember-50 text-ember-800 hover:bg-ember-100'
            : 'border-slate-300 border-dashed bg-surface text-slate-600 hover:bg-slate-50 hover:text-slate-900',
          className,
        )}
      >
        <span>
          {label}
          {summary && <span className="text-ember-400">:</span>}
        </span>
        {summary && <span className="truncate">{summary}</span>}
        <ChevronDown size={13} aria-hidden className="shrink-0 opacity-60" />
      </button>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            style={{ position: 'fixed', top: placement?.top ?? -9999, left: placement?.left ?? -9999 }}
            className="z-[60] flex w-64 animate-pop-in flex-col rounded-lg border border-slate-200 bg-surface shadow-popover"
            onKeyDown={onKeyDown}
          >
            {showSearch && (
              <div className="relative border-b border-slate-100 p-1.5">
                <Search size={14} aria-hidden className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(0);
                  }}
                  placeholder={`Search ${label.toLocaleLowerCase()}…`}
                  aria-label={`Search ${label}`}
                  aria-controls={listId}
                  aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
                  className="h-7 w-full rounded-md bg-slate-50 pr-2 pl-7 text-[13px] placeholder:text-slate-400 focus:bg-surface focus:outline-none focus:ring-2 focus:ring-ember-500/20"
                />
              </div>
            )}
            <ul
              ref={listRef}
              id={listId}
              role="listbox"
              aria-label={label}
              aria-multiselectable="true"
              tabIndex={showSearch ? -1 : 0}
              aria-activedescendant={!showSearch && filtered[active] ? `${listId}-${active}` : undefined}
              className="max-h-64 overflow-y-auto p-1 focus:outline-none"
            >
              {filtered.length === 0 && <li className="px-2 py-3 text-center text-xs text-slate-400">No matches</li>}
              {filtered.map((option, index) => {
                const isSelected = selected.has(option.value);
                return (
                  <li
                    key={option.value}
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => toggle(option.value)}
                    className={clsx(
                      'flex h-8 cursor-pointer items-center gap-2 rounded-md px-2 text-[13px] text-slate-700 select-none',
                      index === active && 'bg-slate-100 text-slate-900',
                    )}
                  >
                    <span
                      aria-hidden
                      className={clsx(
                        'flex size-4 shrink-0 items-center justify-center rounded border',
                        isSelected ? 'border-ember-500 bg-ember-500 text-white' : 'border-slate-300 bg-surface',
                      )}
                    >
                      {isSelected && <Check size={11} strokeWidth={3} />}
                    </span>
                    {option.color && <span aria-hidden className={clsx('size-2 shrink-0 rounded-full', colorClasses(option.color).dot)} />}
                    <span className="flex-1 truncate">{option.label}</span>
                    {option.hint !== undefined && <span className="text-2xs text-slate-400 tabular">{option.hint}</span>}
                  </li>
                );
              })}
            </ul>
            {value.length > 0 && (
              <div className="border-t border-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="flex h-7 w-full items-center justify-center gap-1 rounded-md text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                >
                  <X size={12} aria-hidden /> Clear {label.toLocaleLowerCase()}
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
